import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCompany } from '../context/CompanyContext';
import { getCompanySocialLinks } from '../utils/companySocial';
import { subscribeNewsletter } from '../api';

const NAV = [
  { to: '/', label: 'Inicio' },
  { to: '/catalogo', label: 'Catálogo' },
  { to: '/sobre-nosotros', label: 'Sobre Nosotros' },
  { to: '/contacto', label: 'Contacto' },
];

const CATS = [
  { to: '/catalogo?cat=Protección', label: 'Protección' },
  { to: '/catalogo?cat=Materiales', label: 'Materiales' },
  { to: '/catalogo?cat=Fármacos', label: 'Fármacos' },
  { to: '/catalogo?cat=Instrumental', label: 'Instrumental' },
  { to: '/catalogo?cat=Equipos', label: 'Equipos' },
];

export default function Footer() {
  const { company, companyName, brandInitial, loading: companyLoading } = useCompany();
  const socialLinks = useMemo(() => getCompanySocialLinks(company), [company]);
  const [email, setEmail] = useState('');
  const [ok, setOk] = useState(false);
  const [subscribeLoading, setSubscribeLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setSubscribeLoading(true);
    setError('');
    try {
      const { data } = await subscribeNewsletter({ email: trimmed, source: 'footer' });
      setOk(true);
      setEmail('');
      setTimeout(() => setOk(false), 4000);
      if (data?.message && !data?.already_subscribed) {
        /* feedback visual con ok */
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'No se pudo registrar el correo. Intente más tarde.');
    } finally {
      setSubscribeLoading(false);
    }
  };

  return (
    <div className="np-footer-wrap">
      <footer className="np-footer">
        {/* CTA Newsletter destacado */}
        <div className="np-footer-cta">
          <div>
            <h3>Mantenete al día con novedades dentales</h3>
            <p>Promos exclusivas, nuevos productos y consejos para tu consultorio. Suscribite gratis.</p>
          </div>
          <form onSubmit={submit} style={{ display: 'flex', gap: 8 }}>
            <input
              type="email"
              placeholder="tu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{
                flex: 1, padding: '13px 20px', borderRadius: 9999,
                border: '1px solid rgba(255,255,255,0.15)',
                background: 'rgba(255,255,255,0.08)',
                color: '#fff', fontSize: '0.9rem', outline: 'none',
                fontFamily: 'inherit'
              }}
            />
            <button
              type="submit"
              disabled={subscribeLoading}
              style={{
                padding: '13px 24px', borderRadius: 9999, background: '#fff', color: '#2262e2',
                border: 'none', fontWeight: 700, fontSize: '0.9rem', cursor: subscribeLoading ? 'wait' : 'pointer',
                whiteSpace: 'nowrap', fontFamily: 'inherit', opacity: subscribeLoading ? 0.85 : 1,
              }}
            >
              {subscribeLoading ? 'Enviando…' : ok ? '✓ Suscripto' : 'Suscribirme →'}
            </button>
          </form>
          {error && <p style={{ margin: '8px 0 0', fontSize: '0.78rem', color: '#fecaca' }}>{error}</p>}
          {ok && !error && (
            <p style={{ margin: '8px 0 0', fontSize: '0.78rem', color: '#bbf7d0' }}>
              ¡Listo! Te avisaremos con promociones y novedades.
            </p>
          )}
        </div>

        {/* Grid principal */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 40, paddingBottom: 40 }}>
          {/* Brand */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, minHeight: 36 }}>
              {companyName ? (
                <>
                  <span className="np-footer-brand-mark">{brandInitial}</span>
                  <span style={{ color: '#fff', fontSize: '1.35rem', fontWeight: 800, letterSpacing: '-0.02em' }}>
                    {companyName}
                  </span>
                </>
              ) : companyLoading ? (
                <span className="np-footer-brand-skeleton" aria-hidden />
              ) : null}
            </div>
            <p style={{ fontSize: '0.88rem', lineHeight: 1.65, margin: '0 0 16px', maxWidth: 280, color: 'rgba(255,255,255,0.88)' }}>
              Insumos y equipamiento dental para profesionales. Envíos a consultorio y asesoramiento experto.
            </p>
            {socialLinks.length > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {socialLinks.map((s) => (
                  <a
                    key={s.key}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      width: 36, height: 36, borderRadius: '50%',
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    }}
                    aria-label={s.label}
                    title={s.label}
                  >
                    <i className={`bi ${s.icon}`} style={{ color: '#fff' }} aria-hidden />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Navegación */}
          <div>
            <h6 style={{ color: '#fff', fontWeight: 700, marginBottom: 16, fontSize: '0.9rem' }}>Navegación</h6>
            {NAV.map((l) => (
              <Link key={l.to} to={l.to} style={{ display: 'block', marginBottom: 10, fontSize: '0.88rem' }}>
                {l.label}
              </Link>
            ))}
          </div>

          {/* Categorías */}
          <div>
            <h6 style={{ color: '#fff', fontWeight: 700, marginBottom: 16, fontSize: '0.9rem' }}>Categorías</h6>
            {CATS.map((l) => (
              <Link key={l.to} to={l.to} style={{ display: 'block', marginBottom: 10, fontSize: '0.88rem' }}>
                {l.label}
              </Link>
            ))}
          </div>

          {/* Soporte */}
          <div>
            <h6 style={{ color: '#fff', fontWeight: 700, marginBottom: 16, fontSize: '0.9rem' }}>Soporte</h6>
            <Link to="/contacto" style={{ display: 'block', marginBottom: 10, fontSize: '0.88rem' }}>Contacto</Link>
            <Link to="/mi-cuenta" style={{ display: 'block', marginBottom: 10, fontSize: '0.88rem' }}>Mi cuenta</Link>
            <a href="#" style={{ display: 'block', marginBottom: 10, fontSize: '0.88rem' }}>Envíos</a>
            <a href="#" style={{ display: 'block', marginBottom: 10, fontSize: '0.88rem' }}>Devoluciones</a>
          </div>

          {/* Contacto */}
          <div>
            <h6 style={{ color: '#fff', fontWeight: 700, marginBottom: 16, fontSize: '0.9rem' }}>Contacto</h6>
            <div style={{ fontSize: '0.88rem', lineHeight: 1.9, color: 'rgba(255,255,255,0.88)' }}>
              <div style={{ display: 'flex', alignItems: 'start', gap: 8 }}>
                <i className="bi bi-envelope" style={{ marginTop: 4, color: 'rgba(255,255,255,0.7)' }} />
                <span>{company.contact?.email || 'info@dentalpro.com'}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'start', gap: 8 }}>
                <i className="bi bi-telephone" style={{ marginTop: 4, color: 'rgba(255,255,255,0.7)' }} />
                <span>{company.contact?.phone || '+54 11 0000-0000'}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'start', gap: 8 }}>
                <i className="bi bi-geo-alt" style={{ marginTop: 4, color: 'rgba(255,255,255,0.7)' }} />
                <span>{company.contact?.address || 'Buenos Aires, Argentina'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 24, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12, fontSize: '0.82rem', color: 'rgba(255,255,255,0.75)' }}>
          <div>
            © {new Date().getFullYear()}
            {companyName ? ` ${companyName}.` : ''} Todos los derechos reservados.
          </div>
          <div style={{ display: 'flex', gap: 24 }}>
            <a href="/">Términos</a>
            <a href="/">Privacidad</a>
            <a href="/">Cookies</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
